import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Textarea } from '@/components/ui';
import { addProspect } from '@/data/client';
import { ApiError } from '@/data/api';
import { errorMessage, Modal } from '@/components/crm/Modal';

/* Add Prospect by hand: a business met on a job, a referral, a name from a
   drive past a billboard. It joins the same call list and rotation as a found
   prospect, owned by whoever adds it and due now. A website or a phone number
   is enough; the server dedupes on the domain, or on the number when there is
   no site, and answers a duplicate by pointing at the existing record. */
export function AddProspectModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', phone: '', website: '', category: '', city: '', state: '', note: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () => addProspect({
      name: f.name.trim(), phone: f.phone || undefined, website: f.website || undefined, category: f.category || undefined,
      city: f.city || undefined, state: f.state || undefined, note: f.note || undefined,
    }),
    onSuccess: (r) => {
      for (const k of ['prospects', 'prospectSummary']) void qc.invalidateQueries({ queryKey: [k] });
      navigate(`/prospecting/${r.id}`);
    },
  });
  const existingId = save.error instanceof ApiError && save.error.code === 'exists' ? existingProspectId(save.error) : null;
  const canSave = f.name.trim().length > 0 && (f.phone.trim().length > 0 || f.website.trim().length > 0);

  return (
    <Modal title="Add a prospect" onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>{save.isPending ? 'Adding…' : 'Add to the call list'}</Button>
    </>}>
      <div className="crm-form">
        <p className="crm-cell-sub">Goes into the call queue now, assigned to you. A website or a phone number is enough.</p>
        <Input label="Business name" required autoFocus value={f.name} onChange={set('name')} />
        <div className="crm-grid2">
          <Input label="Phone" inputMode="tel" placeholder="(612) 555-0100" value={f.phone} onChange={set('phone')} />
          <Input label="Website" placeholder="example.com" value={f.website} onChange={set('website')} />
        </div>
        <div className="crm-grid2">
          <Input label="Category" placeholder="Roofers, HVAC…" value={f.category} onChange={set('category')} />
          <div className="crm-grid2">
            <Input label="City" value={f.city} onChange={set('city')} />
            <Input label="State" placeholder="MN" value={f.state} onChange={set('state')} />
          </div>
        </div>
        <Textarea label="Note (optional)" rows={2} placeholder="Where you met them, who to ask for…" value={f.note} onChange={set('note')} />
        {save.error && (
          <p className="crm-error" role="alert">
            {errorMessage(save.error)}{existingId && <> <Link to={`/prospecting/${existingId}`}>Open it</Link>.</>}
          </p>
        )}
      </div>
    </Modal>
  );
}

/* The 409 body carries the existing prospect's id beside the message. */
function existingProspectId(error: ApiError): string | null {
  try { return (JSON.parse(error.message.replace(/^API \d+: /, '')) as { prospectId?: string }).prospectId ?? null; }
  catch { return null; }
}
